import { Host, h } from "@stencil/core";
import monaco, { configureMonacoYaml } from "../../common/monaco/output/monaco.js";
let autoId = 0;
export class ChCodeEditor {
    constructor() {
        this.language = undefined;
        this.options = {
            automaticLayout: true,
            mouseWheelScrollSensitivity: 4,
            mouseWheelZoom: true,
            tabSize: 2
        };
        this.readonly = false;
        this.theme = "vs";
        this.value = undefined;
        this.yamlSchemaUri = "";
    }
    #monacoEditorInstance;
    #resizeObserver;
    #editorId = "";
    // Refs
    #monacoRef;
    #absoluteContentRef;
    languageChanged(newLanguage) {
        monaco.editor.setModelLanguage(this.#monacoEditorInstance.getModel(), newLanguage);
    }
    optionsChanged(options) {
        this.#monacoEditorInstance?.updateOptions({
            options
        });
    }
    readonlyChanged(newReadonly) {
        this.#monacoEditorInstance?.updateOptions({
            readOnly: this.options.readOnly ?? newReadonly
        });
    }
    themeChanged(newTheme) {
        this.#monacoEditorInstance.updateOptions({ theme: newTheme });
    }
    valueChange(newValue) {
        this.#monacoEditorInstance?.setValue(newValue);
    }
    yamlSchemaUriChange(newUri) {
        if (this.language !== "yaml") {
            return;
        }
        // Necessary to not combine the current scheme with the new scheme
        this.#editorId = `ch-editor-${autoId++}`;
        if (newUri) {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: this.#getYamlSchemas()
            });
        }
        else {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: []
            });
        }
        this.#monacoEditorInstance.setModel(monaco.editor.createModel(this.value, this.language, monaco.Uri.parse(`file:///${this.#editorId}.txt`)));
    }
    connectedCallback() {
        this.#editorId = `ch-editor-${autoId++}`;
    }
    componentDidLoad() {
        this.#configureYaml();
        this.#setupNormalEditor();
        this.#resizeObserver = new ResizeObserver(entries => {
            const absoluteContentEntry = entries[0].contentRect;
            this.#monacoEditorInstance.layout({
                width: absoluteContentEntry.width,
                height: absoluteContentEntry.height
            });
        });
        this.#resizeObserver.observe(this.#absoluteContentRef);
    }
    disconnectedCallback() {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
    }
    /**
     * Update the editor's options after the editor has been created.
     * @param options Set of options to be updated
     */
    async updateOptions(options) {
        this.#monacoEditorInstance?.updateOptions(options);
    }
    #getYamlSchemas = () => [
        {
            // If YAML file is opened matching this glob
            fileMatch: [`**/${this.#editorId}.*`],
            // Then this schema will be downloaded from the internet and used.
            uri: this.yamlSchemaUri
        }
    ];
    #setupNormalEditor() {
        const editorModel = monaco.editor.createModel(this.value, this.language, monaco.Uri.parse(`file:///${this.#editorId}.txt`));
        this.#monacoEditorInstance = monaco.editor.create(this.#monacoRef, {
            ...this.options,
            theme: this.options.theme ?? this.theme,
            readOnly: this.options.readOnly ?? this.readonly,
            model: editorModel
        });
    }
    #configureYaml() {
        if (this.language === "yaml" && !!this.yamlSchemaUri) {
            configureMonacoYaml(monaco, {
                enableSchemaRequest: true,
                format: true,
                schemas: this.#getYamlSchemas()
            });
        }
    }
    render() {
        return (h(Host, { key: '2a86c95bff3941a8491ef8029925648e597af94a' }, h("div", { key: '5679f42138f13fa12c2c7b245caf7521720f05d7', class: "ch-code-editor-absolute-content", ref: el => (this.#absoluteContentRef = el) }, h("div", { key: '71956b02979dc27f330ab0547e4737cc1c656fe7', ref: el => (this.#monacoRef = el) }))));
    }
    static get is() { return "ch-code-editor"; }
    static get originalStyleUrls() {
        return {
            "$": ["code-editor.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["code-editor.css"]
        };
    }
    static get properties() {
        return {
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the language of the auto created model in the editor."
                },
                "attribute": "language",
                "reflect": false
            },
            "options": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "CodeEditorOptions",
                    "resolved": "CodeEditorOptions",
                    "references": {
                        "CodeEditorOptions": {
                            "location": "import",
                            "path": "./code-editor-types.js",
                            "id": "src/components/code-editor/code-editor-types.ts::CodeEditorOptions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the editor options."
                },
                "defaultValue": "{\n    automaticLayout: true,\n    mouseWheelScrollSensitivity: 4,\n    mouseWheelZoom: true,\n    tabSize: 2\n  }"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the editor should be readonly.\nIf the `readOnly` property is specified in the `options` property,\nthis property has no effect."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme to be used for rendering."
                },
                "attribute": "theme",
                "reflect": false,
                "defaultValue": "\"vs\""
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the value of the editor."
                },
                "attribute": "value",
                "reflect": false
            },
            "yamlSchemaUri": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the schema URI for the YAML language."
                },
                "attribute": "yaml-schema-uri",
                "reflect": false,
                "defaultValue": "\"\""
            }
        };
    }
    static get methods() {
        return {
            "updateOptions": {
                "complexType": {
                    "signature": "(options: CodeEditorOptions) => Promise<void>",
                    "parameters": [{
                            "name": "options",
                            "type": "CodeEditorOptions",
                            "docs": "Set of options to be updated"
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "CodeEditorOptions": {
                            "location": "import",
                            "path": "./code-editor-types.js",
                            "id": "src/components/code-editor/code-editor-types.ts::CodeEditorOptions"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the editor's options after the editor has been created.",
                    "tags": [{
                            "name": "param",
                            "text": "options Set of options to be updated"
                        }]
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "languageChanged"
            }, {
                "propName": "options",
                "methodName": "optionsChanged"
            }, {
                "propName": "readonly",
                "methodName": "readonlyChanged"
            }, {
                "propName": "theme",
                "methodName": "themeChanged"
            }, {
                "propName": "value",
                "methodName": "valueChange"
            }, {
                "propName": "yamlSchemaUri",
                "methodName": "yamlSchemaUriChange"
            }];
    }
}
